import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {alertModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";

const client = new MdbsCore.StudyDefinitionClient();
const auditLogClient = new MdbsCore.AuditLogClient();

// Audit-log categories written by the study-definition export handlers (see eCRF Core).
const EXPORT_CATEGORIES = ["StudyDefinitionExport", "StudyDefinitionSeederExport"];
// Audit-log category written when a study-definition report file is downloaded.
const REPORT_DOWNLOAD_CATEGORY = ["StudyDefinitionReport"];
// Audit-log category written by the study-definition importer.
const IMPORT_CATEGORY = ["StudyDefinitionImport"];

@Module({namespaced: true, name: 'studyDefinitionAdmin', store: store})
export default class StudyDefinitionAdminModule extends BaseModule {
    private _importReport: MdbsCore.StudyDefinitionImportReportDto | null = null;
    private _history: MdbsCore.StudyDefinitionHistoryEntryDto[] = [];
    private _exportHistory: MdbsCore.AuditLogEntryDto[] = [];
    private _downloadHistory: MdbsCore.AuditLogEntryDto[] = [];
    private _seededVersions: MdbsCore.SeededStudyDefinitionVersionDto[] = [];

    get importReport(): MdbsCore.StudyDefinitionImportReportDto | null {
        return this._importReport;
    }

    get history(): MdbsCore.StudyDefinitionHistoryEntryDto[] {
        return this._history;
    }

    get exportHistory(): MdbsCore.AuditLogEntryDto[] {
        return this._exportHistory;
    }

    get downloadHistory(): MdbsCore.AuditLogEntryDto[] {
        return this._downloadHistory;
    }

    get seededVersions(): MdbsCore.SeededStudyDefinitionVersionDto[] {
        return this._seededVersions;
    }

    @Mutation
    setImportReport(report: MdbsCore.StudyDefinitionImportReportDto | null) {
        this._importReport = report;
    }

    @Mutation
    setHistory(entries: MdbsCore.StudyDefinitionHistoryEntryDto[]) {
        this._history = entries;
    }

    @Mutation
    setExportHistory(entries: MdbsCore.AuditLogEntryDto[]) {
        this._exportHistory = entries;
    }

    @Mutation
    setDownloadHistory(entries: MdbsCore.AuditLogEntryDto[]) {
        this._downloadHistory = entries;
    }

    @Mutation
    setSeededVersions(entries: MdbsCore.SeededStudyDefinitionVersionDto[]) {
        this._seededVersions = entries;
    }

    @Mutation
    reset() {
        this._importReport = null;
        this._history = [];
        this._exportHistory = [];
        this._downloadHistory = [];
        this._seededVersions = [];
    }

    @Action({rawError: true})
    async exportDefinition(studyDefinitionId: string) {
        this.startLoading();
        try {
            return await client.exportStudyDefinition(studyDefinitionId);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async exportSeeder(studyDefinitionId: string) {
        this.startLoading();
        try {
            return await client.exportStudySeederDefinition(studyDefinitionId);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async updateDates(payload: { studyDefinitionId: string, ethicDate?: Date, goLiveDate?: Date }) {
        this.startLoading();
        try {
            const dto = new MdbsCore.StudyDefinitionDatesUpdateDto({
                ethicDate: payload.ethicDate,
                goLiveDate: payload.goLiveDate
            });
            const result = await client.updateDates(payload.studyDefinitionId, dto);
            return result;
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async loadHistory() {
        this.startLoading();
        try {
            const entries = await client.getImportHistory();
            this.setHistory(entries ?? []);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async loadExportHistory() {
        this.startLoading();
        try {
            const result = await auditLogClient.getAuditLogEntries(
                0, 200, EXPORT_CATEGORIES, undefined, undefined, undefined, undefined);
            this.setExportHistory(result?.items ?? []);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async loadSeededVersions() {
        try {
            const versions = await client.getSeededVersions();
            this.setSeededVersions(versions ?? []);
        } catch (e) {
            await alertModule.handleException(e);
        }
    }

    @Action({rawError: true})
    async loadDownloadHistory() {
        this.startLoading();
        try {
            const result = await auditLogClient.getAuditLogEntries(
                0, 200, REPORT_DOWNLOAD_CATEGORY, undefined, undefined, undefined, undefined);
            this.setDownloadHistory(result?.items ?? []);
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }

    @Action({rawError: true})
    async exportImportHistory() {
        return this.exportAuditLog(IMPORT_CATEGORY);
    }

    @Action({rawError: true})
    async exportExportHistory() {
        return this.exportAuditLog(EXPORT_CATEGORIES);
    }

    @Action({rawError: true})
    async exportDownloadHistory() {
        return this.exportAuditLog(REPORT_DOWNLOAD_CATEGORY);
    }

    @Action({rawError: true})
    async exportAuditLog(identifiers: string[]) {
        try {
            return await auditLogClient.exportAuditLog(identifiers, undefined, undefined, undefined, undefined);
        } catch (e) {
            await alertModule.handleException(e);
        }
    }

    @Action({rawError: true})
    async downloadReportFile(fileName: string) {
        try {
            return await client.downloadImportReportFile(fileName);
        } catch (e) {
            await alertModule.handleException(e);
        }
    }

    @Action({rawError: true})
    async importDefinition(payload: { testRun: boolean, file: File }) {
        this.startLoading();
        this.setImportReport(null);
        try {
            const report = await client.importStudyDefinition(payload.testRun,
                {data: payload.file, fileName: payload.file.name});
            this.setImportReport(report);
            return report;
        } catch (e) {
            await alertModule.handleException(e);
        } finally {
            this.stopLoading();
        }
    }
}
